import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  useMediaQuery,
} from "@material-ui/core";
import { addDays, addMonths, format, getDate, startOfMonth } from "date-fns";

import { ChevronLeftIcon, ChevronRightIcon } from "../../../components/common/iconography";
import { StorageFrequency } from "../../resources/accounts/chargeRules/chargeRuleConstants";

const DATE_FORMAT = "yyyy-MM-dd";

const toDateString = (date) => format(date, DATE_FORMAT);

const getMonthStart = (date) => startOfMonth(date);

const getDay = (date) => getDate(date);

const getLatestCompletedPeriod = (frequency, today = new Date()) => {
  const monthStart = getMonthStart(today);
  const day = getDay(today);

  if (frequency === StorageFrequency.MONTHLY) {
    return {
      start: getMonthStart(addMonths(today, -1)),
      end: monthStart,
    };
  }

  if (frequency === StorageFrequency.BIWEEKLY) {
    if (day < 15) {
      const previousMonthStart = getMonthStart(addMonths(today, -1));
      return {
        start: addDays(previousMonthStart, 14), // 15th of previous month
        end: monthStart, // 1st of current month (exclusive)
      };
    }

    return {
      start: monthStart, // 1st of current month
      end: addDays(monthStart, 14), // 15th of current month (exclusive)
    };
  }

  if (frequency === StorageFrequency.WEEKLY) {
    if (day < 8) {
      const previousMonthStart = getMonthStart(addMonths(today, -1));
      return {
        start: addDays(previousMonthStart, 21), // 22nd of previous month
        end: monthStart, // 1st of current month (exclusive)
      };
    }

    if (day < 15) {
      return {
        start: monthStart, // 1st
        end: addDays(monthStart, 7), // 8th (exclusive)
      };
    }

    if (day < 22) {
      return {
        start: addDays(monthStart, 7), // 8th
        end: addDays(monthStart, 14), // 15th (exclusive)
      };
    }

    return {
      start: addDays(monthStart, 14), // 15th
      end: addDays(monthStart, 21), // 22nd (exclusive)
    };
  }

  return { start: null, end: null };
};

const getPreviousPeriod = (period, frequency) => {
  if (!period?.start || !period?.end) return period;

  const { start } = period;
  const startDay = getDay(start);
  const monthStart = getMonthStart(start);

  if (frequency === StorageFrequency.MONTHLY) {
    return {
      start: getMonthStart(addMonths(start, -1)),
      end: monthStart,
    };
  }

  if (frequency === StorageFrequency.BIWEEKLY) {
    if (startDay === 1) {
      const previousMonthStart = getMonthStart(addMonths(start, -1));
      return {
        start: addDays(previousMonthStart, 14), // 15th previous month
        end: monthStart,
      };
    }

    return {
      start: monthStart,
      end: addDays(monthStart, 14), // 15th current month
    };
  }

  if (frequency === StorageFrequency.WEEKLY) {
    if (startDay === 1) {
      const previousMonthStart = getMonthStart(addMonths(start, -1));
      return {
        start: addDays(previousMonthStart, 21), // 22nd previous month
        end: monthStart,
      };
    }

    if (startDay === 8) {
      return {
        start: monthStart,
        end: addDays(monthStart, 7),
      };
    }

    if (startDay === 15) {
      return {
        start: addDays(monthStart, 7),
        end: addDays(monthStart, 14),
      };
    }

    if (startDay === 22) {
      return {
        start: addDays(monthStart, 14),
        end: addDays(monthStart, 21),
      };
    }
  }

  return period;
};

const getNextPeriod = (period, frequency) => {
  if (!period?.start || !period?.end) return period;

  const { start, end } = period;
  const startDay = getDay(start);

  if (frequency === StorageFrequency.MONTHLY) {
    return {
      start: end,
      end: getMonthStart(addMonths(end, 1)),
    };
  }

  if (frequency === StorageFrequency.BIWEEKLY) {
    if (startDay === 1) {
      return {
        start: addDays(start, 14), // 15th of same month
        end: getMonthStart(addMonths(start, 1)), // 1st next month
      };
    }

    const nextMonthStart = getMonthStart(addMonths(start, 1));
    return {
      start: nextMonthStart,
      end: addDays(nextMonthStart, 14), // 15th of next month
    };
  }

  if (frequency === StorageFrequency.WEEKLY) {
    if (startDay === 1) {
      return {
        start: addDays(start, 7),
        end: addDays(start, 14),
      };
    }

    if (startDay === 8) {
      return {
        start: addDays(start, 7),
        end: addDays(start, 14),
      };
    }

    if (startDay === 15) {
      return {
        start: addDays(start, 7),
        end: getMonthStart(addMonths(start, 1)),
      };
    }

    if (startDay === 22) {
      const nextMonthStart = getMonthStart(addMonths(start, 1));
      return {
        start: nextMonthStart,
        end: addDays(nextMonthStart, 7),
      };
    }
  }

  return period;
};

const getPeriodAtOffset = (latestPeriod, frequency, offset) => {
  if (!latestPeriod?.start || !latestPeriod?.end || !frequency) {
    return { start: null, end: null };
  }

  if (offset === 0) {
    return latestPeriod;
  }

  let period = latestPeriod;

  if (offset < 0) {
    for (let i = 0; i < Math.abs(offset); i += 1) {
      period = getPreviousPeriod(period, frequency);
    }
    return period;
  }

  for (let i = 0; i < offset; i += 1) {
    period = getNextPeriod(period, frequency);
  }

  return period;
};

const getDisplayLabel = (startDate, endDateExclusive, mdDown) => {
  if (!startDate || !endDateExclusive) return "--";
  const endDateInclusive = addDays(endDateExclusive, -1);
  const dateFormat = mdDown ? "M/d/yy" : "MM/dd/yyyy";
  return `${format(startDate, dateFormat)} - ${format(endDateInclusive, dateFormat)}`;
};

export const BillingPeriodSelector = ({
  label,
  frequency,
  onPeriodChange,
  disabled = false,
  fullWidth = false,
}) => {
  const [offset, setOffset] = useState(0);
  const mdDown = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const latestPeriod = useMemo(
    () => getLatestCompletedPeriod(frequency),
    [frequency],
  );

  const selectedPeriod = useMemo(
    () => getPeriodAtOffset(latestPeriod, frequency, offset),
    [latestPeriod, frequency, offset],
  );

  useEffect(() => {
    setOffset(0);
  }, [frequency]);

  useEffect(() => {
    if (!frequency || !selectedPeriod?.start || !selectedPeriod?.end) {
      onPeriodChange({ startDate: "", endDate: "" });
      return;
    }

    onPeriodChange({
      startDate: toDateString(selectedPeriod.start),
      endDate: toDateString(selectedPeriod.end),
    });
  }, [frequency, selectedPeriod, onPeriodChange]);

  const canGoForward = offset < 0;
  const periodLabel = getDisplayLabel(
    selectedPeriod.start,
    selectedPeriod.end,
    mdDown,
  );

  return (
    <Box display="flex" alignItems="center" mt={1}>
      <TextField
        label={label}
        value={periodLabel}
        variant="outlined"
        size="small"
        fullWidth={fullWidth}
        inputProps={{ readOnly: true }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconButton
                size="small"
                onClick={() => setOffset((value) => value - 1)}
                disabled={disabled || !frequency}
                aria-label="Previous billing period"
              >
                <ChevronLeftIcon fontSize="large" />
              </IconButton>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => setOffset((value) => Math.min(value + 1, 0))}
                disabled={disabled || !frequency || !canGoForward}
                aria-label="Next billing period"
              >
                <ChevronRightIcon fontSize="large" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};
